import {
  achievementApi,
  type ConceptChipScoredItemsParams,
  type GroupConceptChipAchievement,
  type GroupConceptChipAchievementLog,
  type GroupConceptChipAchievementLogsParams,
  type GroupConceptChipAchievementsParams,
  type GroupConceptChipDetailParams,
  type StudentConceptChipAchievementLogsParams,
  type StudentConceptChipAchievementsParams,
  type StudentsAchievementSummaryParams,
} from '@mathflat/domain/Achievement'
import {
  challengeApi,
  type LessonClassChallengeSetting,
  type LessonClassChallengeSettingBody,
  type LessonClassChallengeSettingStatusBody,
} from '@mathflat/domain/Challenge'
import { conceptChipApi, type ConceptChipsParams } from '@mathflat/domain/ConceptChip'
import {
  derivationApi,
  type GroupCommonWeakConceptChipParams,
  type PersonalizedWeakConceptChipParams,
  type PersonalizedWeakConceptChipWorksheetBody,
} from '@mathflat/domain/Derivation'
import {
  type AchievementReport as AchievementReportData,
  type AchievementReportCreateBody,
  reportApi,
} from '@mathflat/domain/Report'
import type { Student } from 'src/dto'

import { makeAdapter } from '@/controller/Adapter.ts'
import { AchievementConceptChip } from '@/dto/AchievementAnalysis/AchievementConceptChip.dto.ts'
import {
  AchievementReport,
  AchievementReportColl,
} from '@/dto/AchievementAnalysis/AchievementReport.dto.ts'
import { GroupAchievementConceptChipDetail } from '@/dto/AchievementAnalysis/GroupAchievementConceptChipDetail.dto.ts'
import type { LessonClass } from '@/dto/LessonClass/LessonClass.ts'
import CheckableMap from '@/models/CheckableMap.ts'

export type LessonClassChallengeParams = LessonClassChallengeSettingBody & {
  lessonClassId: LessonClass['id']
}

class AchievementAnalysisController {
  adapter = {
    report: makeAdapter({
      from(data: AchievementReportData & { studentName: Student['name'] }): AchievementReport {
        return new AchievementReport(data)
      },
    }),

    groupAchievementConceptChips: {
      from(
        data: (GroupConceptChipAchievement | GroupConceptChipAchievementLog)[],
      ): AchievementConceptChip[] {
        return data
          .map(
            ({ averageAchievementLevel, ...chip }) =>
              new AchievementConceptChip({
                ...chip,
                id: chip.conceptChipId,
                achievementLevel: averageAchievementLevel,
              }),
          )
          .sort((a, b) => a.orderingNumber - b.orderingNumber)
      },
    },
  }

  /// 그룹 유형 분석

  getGroupConceptChipsAndAchievements = async (params: GroupConceptChipAchievementsParams) => {
    const data = await achievementApi.getGroupConceptChipAchievements(params)
    return this.adapter.groupAchievementConceptChips.from(data)
  }

  getGroupConceptChipDetail = async (params: GroupConceptChipDetailParams) => {
    const data = await achievementApi.getGroupConceptChipDetail(params)
    return new GroupAchievementConceptChipDetail(data)
  }

  getGroupPastAchievements = async (params: GroupConceptChipAchievementLogsParams) => {
    const data = await achievementApi.getGroupConceptChipAchievementLogs(params)
    return this.adapter.groupAchievementConceptChips.from(data)
  }

  getGroupPersonalizedWeakConceptChip = async (params: PersonalizedWeakConceptChipParams) => {
    return derivationApi.getPersonalizedWeakConceptChipRecommendation(params)
  }

  postGroupPersonalizedWeakConceptChip = async (
    params: PersonalizedWeakConceptChipWorksheetBody,
  ) => {
    return derivationApi.createPersonalizedWeakConceptChipWorksheets(params)
  }

  getGroupCommonWeakConceptChip = async (params: GroupCommonWeakConceptChipParams) => {
    return derivationApi.getGroupCommonWeakConceptChipRecommendation(params)
  }

  /// 개인 유형 분석

  getConceptChips = async (params: ConceptChipsParams) =>
    await conceptChipApi.getConceptChips(params, {
      mp_event_name: 'T46-01_유형분석_유형칩_조회_API',
    })

  getAchievements = async (
    params: StudentConceptChipAchievementsParams,
    studentId: Student['id'],
  ) =>
    await achievementApi.getStudentConceptChipAchievements(studentId, params, {
      mp_event_name: 'T46-01_유형분석_학생성취도_조회_API',
    })

  getStudentsAchievementSummary = async (
    studentIds: Student['id'][],
    params: StudentsAchievementSummaryParams,
  ) => await achievementApi.getStudentsAchievementSummary(studentIds, params)

  getPastAchievements = async (
    params: StudentConceptChipAchievementLogsParams,
    studentId: Student['id'],
  ) =>
    await achievementApi.getStudentConceptChipAchievementLogs(studentId, params, {
      mp_event_name: 'T46-01_유형분석_학생성취도_과거이력_조회_API',
    })

  getAchievementStudyLogs = async (
    studentId: Student['id'],
    conceptChipId: AchievementConceptChip['conceptChipId'],
    params: ConceptChipScoredItemsParams = {},
  ) =>
    await achievementApi.getConceptChipScoredItems(studentId, conceptChipId, params, {
      mp_event_name: 'T46-01_유형분석_유형칩별_상세보기_API',
    })

  createReport = async (studentId: Student['id'], report: AchievementReportCreateBody) => {
    await reportApi.createAchievementReport(studentId, report)
  }

  getAchievementReport = async (studentId: Student['id']) => {
    const { studentName, reportInfo } = await reportApi.getStudentAchievementReports(studentId, {
      mp_event_name: 'T46-01_유형분석_보고서_조회_API',
    })
    const parsedData = reportInfo.map((item) => this.adapter.report.from({ ...item, studentName }))
    return new AchievementReportColl(CheckableMap.fromArr(parsedData, 'id'))
  }

  deleteReports = async (reportIds: AchievementReport['id'][]) => {
    await reportApi.deleteAchievementReports(
      { reportIds },
      { mp_event_name: 'T46-01_유형분석_보고서_삭제_API' },
    )
  }

  async preview({ reportId }: { reportId: AchievementReport['id'] }) {
    return await reportApi.getAchievementReportFile({ reportId, presignedUrlType: 'PREVIEW' })
  }

  postLessonClassChallenges = async ({ lessonClassId, ...setting }: LessonClassChallengeParams) =>
    await challengeApi.saveLessonClassChallengeSetting(lessonClassId, setting)

  getLessonClassChallenges = async (lessonClassId: LessonClass['id']) =>
    await challengeApi.getLessonClassChallengeSettings(lessonClassId)

  patchLessonClassChallengeStatus = async (setting: {
    id: LessonClassChallengeSetting['id']
    status: Extract<LessonClassChallengeSettingStatusBody['status'], 'ACTIVE' | 'INACTIVE'>
  }) => {
    await challengeApi.updateLessonClassChallengeSettingStatus(setting)
  }
}

export const achievementAnalysisController = new AchievementAnalysisController()
